// client/src/components/pages/WeighbridgeCompliance/AssetRegister.tsx
// (converted from AssetRegister.js, ST-00259)
//
// ST-00259: toasts now render (AppToaster), so the list's load failure carries a stable
// toastId: every (debounced) search re-asks for the list, and a failing endpoint used to
// stack one identical "Failed to load assets" per search. Nothing else changed.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import type { AxiosResponse } from 'axios';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Scale,
  Eye,
  X,
  ShieldCheck,
  Users,
  Wrench,
  CheckSquare,
  Square,
  MinusSquare,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { parseISO, differenceInDays } from 'date-fns';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import AssetModalJs from './AssetModal';
import type {
  AssetCalibrationRow,
  AssetDetailBody,
  AssetListParams,
  AssetOperator,
  MaintenanceRow,
  PaginatedBody,
  WeighbridgeAssetRow,
} from '../../../types/clientPages/weighbridgeCompliance';

// AssetModal is still JS: a typed view of its props.
const AssetModal = AssetModalJs as unknown as ComponentType<{
  asset: WeighbridgeAssetRow | null;
  onClose: () => void;
  onSaved: () => void;
}>;

/** The rows of a paginated `{ data }` body or of a bare array. */
const listOf = <T,>(res: AxiosResponse<PaginatedBody<T> | T[]>): T[] => {
  const d = res.data;
  return Array.isArray(d) ? d : d?.data || [];
};

interface BadgeStyle {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

/** The compliance badge's keys: the server's verdict, or the client's fallback. */
type ComplianceBadgeStatus = 'compliant' | 'expiring' | 'expired' | 'non-compliant' | 'unknown';

const ACTIVE_BADGE: BadgeStyle = {
  bg: 'bg-emerald-50',
  text: 'text-emerald-700',
  border: 'border-emerald-200',
  dot: 'bg-emerald-500',
};

const StatusBadge = ({ status }: { status: string }) => {
  const configs: Record<string, BadgeStyle> = {
    Active: ACTIVE_BADGE,
    'Out of Service': {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    Decommissioned: {
      bg: 'bg-slate-100',
      text: 'text-slate-500',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
  };
  const config = configs[status] || ACTIVE_BADGE;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status}
    </span>
  );
};

const UNKNOWN_COMPLIANCE: BadgeStyle & { label: string } = {
  bg: 'bg-slate-100',
  text: 'text-slate-500',
  border: 'border-slate-200',
  dot: 'bg-slate-400',
  label: 'Unknown',
};

const ComplianceBadge = ({ status }: { status: string }) => {
  const configs: Record<string, BadgeStyle & { label: string }> = {
    compliant: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500',
      label: 'Compliant',
    },
    expiring: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
      label: 'Expiring Soon',
    },
    expired: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      dot: 'bg-red-500',
      label: 'Expired',
    },
    'non-compliant': {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      dot: 'bg-red-500',
      label: 'Non-Compliant',
    },
    unknown: UNKNOWN_COMPLIANCE,
  };
  const config = configs[status] || UNKNOWN_COMPLIANCE;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

interface AssetDetailPanelProps {
  asset: WeighbridgeAssetRow;
  onClose: () => void;
}

const AssetDetailPanel = ({ asset, onClose }: AssetDetailPanelProps) => {
  const [calibrations, setCalibrations] = useState<AssetCalibrationRow[]>([]);
  const [operators, setOperators] = useState<AssetOperator[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const [calRes, maintRes] = await Promise.all([
          api.calibrations.getByAsset(asset.id) as Promise<
            AxiosResponse<PaginatedBody<AssetCalibrationRow> | AssetCalibrationRow[]>
          >,
          api.maintenance.getByAsset(asset.id) as Promise<
            AxiosResponse<PaginatedBody<MaintenanceRow> | MaintenanceRow[]>
          >,
        ]);
        setCalibrations(listOf(calRes));
        setMaintenance(listOf(maintRes));

        try {
          const opsRes = (await api.assets.get(asset.id)) as AxiosResponse<AssetDetailBody>;
          setOperators(opsRes.data?.operators || []);
        } catch {
          setOperators([]);
        }
      } catch {
        toast.error('Failed to load asset details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [asset.id]);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2
              className="text-lg font-bold text-slate-800"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {asset.name}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">{asset.location_name || '-'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Manufacturer
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.manufacturer || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Model
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.model || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Serial Number
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.serial_number || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Capacity
              </p>
              <p className="text-sm text-slate-700 mt-1">
                {asset.capacity_min || '-'} - {asset.capacity_max || '-'} kg
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Scale Interval
              </p>
              <p className="text-sm text-slate-700 mt-1">
                {asset.scale_interval ? `${asset.scale_interval} kg` : '-'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Accuracy Class
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.accuracy_class || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Installation Type
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.installation_type || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                NRCS Type Approval
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.nrcs_type_approval || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Status
              </p>
              <div className="mt-1">
                <StatusBadge status={asset.status || 'Active'} />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Deck Dimensions
              </p>
              <p className="text-sm text-slate-700 mt-1">
                {asset.deck_length && asset.deck_width
                  ? `${asset.deck_length}m x ${asset.deck_width}m`
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Load Cells
              </p>
              <p className="text-sm text-slate-700 mt-1">{asset.num_load_cells || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Installation Date
              </p>
              <p className="text-sm text-slate-700 mt-1">
                {asset.installation_date ? formatAppDate(asset.installation_date) : '-'}
              </p>
            </div>
          </div>

          {asset.indicator_make && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Indicator Details
              </h4>
              <div className="grid grid-cols-3 gap-4 bg-slate-50 rounded-lg p-3">
                <div>
                  <p className="text-[11px] text-slate-400">Make</p>
                  <p className="text-sm text-slate-700">{asset.indicator_make}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">Model</p>
                  <p className="text-sm text-slate-700">{asset.indicator_model || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">Serial</p>
                  <p className="text-sm text-slate-700">{asset.indicator_serial || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {asset.notes && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Notes
              </h4>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{asset.notes}</p>
            </div>
          )}

          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-slate-200 rounded w-1/3" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
            </div>
          ) : (
            <>
              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Calibrations & Verifications
                </h4>
                {calibrations.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No calibration records found</p>
                ) : (
                  <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50/80 border-b border-slate-100">
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Type
                          </th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Cert No
                          </th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Expiry
                          </th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Result
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {calibrations.map((cal) => (
                          <tr key={cal.id} className="hover:bg-primary-50/30 transition-colors">
                            <td className="px-4 py-2.5 text-sm text-slate-700">
                              {cal.certificate_type || '-'}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-600">
                              {cal.certificate_number || '-'}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-600">
                              {cal.next_due_date ? formatAppDate(cal.next_due_date) : '-'}
                            </td>
                            <td className="px-4 py-2.5 text-sm">
                              {cal.status === 'Pass' ? (
                                <span className="text-emerald-600 font-medium">Pass</span>
                              ) : cal.status === 'Fail' ? (
                                <span className="text-red-600 font-medium">Fail</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Assigned Operators
                </h4>
                {operators.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No operators assigned</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {operators.map((op) => (
                      <span
                        key={op.id}
                        className="inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border-blue-200"
                      >
                        {/* TODO: original intent was op.firstName + ' ' + op.lastName; operators expose full_name only */}
                        {op.name || op.full_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4" /> Maintenance History
                </h4>
                {maintenance.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No maintenance records found</p>
                ) : (
                  <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50/80 border-b border-slate-100">
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Type
                          </th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Date
                          </th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {maintenance.map((m) => (
                          <tr key={m.id} className="hover:bg-primary-50/30 transition-colors">
                            <td className="px-4 py-2.5 text-sm text-slate-700">
                              {m.maintenance_type || '-'}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-600">
                              {m.completed_date
                                ? formatAppDate(m.completed_date)
                                : m.scheduled_date
                                  ? formatAppDate(m.scheduled_date)
                                  : '-'}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-600">
                              {m.status || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AssetRegister = () => {
  const [assets, setAssets] = useState<WeighbridgeAssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAsset, setEditAsset] = useState<WeighbridgeAssetRow | null>(null);
  const [detailAsset, setDetailAsset] = useState<WeighbridgeAssetRow | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params: AssetListParams = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const res = (await api.assets.getAll(params)) as AxiosResponse<
        PaginatedBody<WeighbridgeAssetRow> | WeighbridgeAssetRow[]
      >;
      setAssets(listOf(res));
    } catch {
      // One message however many searches fail while it is on screen (ST-00259).
      toast.error('Failed to load assets', { toastId: 'compliance-assets-load' });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleDelete = async (id: number) => {
    if (
      !window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')
    )
      return;
    setDeleting(id);
    try {
      await api.assets.delete(id);
      toast.success('Asset deleted');
      fetchAssets();
    } catch {
      toast.error('Failed to delete asset');
    } finally {
      setDeleting(null);
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditAsset(null);
    fetchAssets();
  };

  const getComplianceStatus = (asset: WeighbridgeAssetRow): ComplianceBadgeStatus => {
    if (asset.compliance_status) return asset.compliance_status;
    if (!asset.next_cert_due_date) return 'unknown';
    const days = differenceInDays(parseISO(asset.next_cert_due_date), new Date());
    if (days <= 0) return 'expired';
    if (days <= 90) return 'expiring';
    return 'compliant';
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === assets.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(assets.map((a) => a.id)));
    }
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Change status of ${count} asset(s) to "${newStatus}"?`)) return;
    setBulkProcessing(true);
    let success = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        await api.assets.update(id, { status: newStatus });
        success++;
      } catch {
        failed++;
      }
    }
    setBulkProcessing(false);
    setSelected(new Set());
    if (success > 0) toast.success(`${success} asset(s) updated to "${newStatus}"`);
    if (failed > 0) toast.error(`${failed} asset(s) failed to update`);
    fetchAssets();
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Delete ${count} asset(s)? This cannot be undone.`)) return;
    setBulkProcessing(true);
    let success = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        await api.assets.delete(id);
        success++;
      } catch {
        failed++;
      }
    }
    setBulkProcessing(false);
    setSelected(new Set());
    if (success > 0) toast.success(`${success} asset(s) deleted`);
    if (failed > 0) toast.error(`${failed} asset(s) failed to delete`);
    fetchAssets();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Asset Register
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage your weighbridge assets and equipment
          </p>
        </div>
        <button
          onClick={() => {
            setEditAsset(null);
            setShowModal(true);
          }}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Add Weighbridge
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            placeholder="Search assets..."
          />
        </div>
        <div className="relative">
          <ThemedSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            className="appearance-none pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm text-slate-600"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Out of Service">Out of Service</option>
            <option value="Decommissioned">Decommissioned</option>
          </ThemedSelect>
        </div>
      </div>

      {loading ? (
        <div className="bg-surface rounded-xl border border-slate-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/3" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-full" />
          </div>
        </div>
      ) : assets.length === 0 ? (
        <div className="bg-surface rounded-xl border border-slate-200">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
              <Scale className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">
              No weighbridge assets found
            </h3>
            <p className="text-sm text-slate-400 max-w-sm">
              {search || statusFilter
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first weighbridge asset'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 px-5 py-3 bg-teal-50 border-b border-teal-200">
              <span className="text-sm font-semibold text-teal-800">{selected.size} selected</span>
              <div className="h-5 w-px bg-teal-200" />
              <button
                onClick={() => handleBulkStatusChange('Active')}
                disabled={bulkProcessing}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-50"
              >
                Set Active
              </button>
              <button
                onClick={() => handleBulkStatusChange('Out of Service')}
                disabled={bulkProcessing}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors disabled:opacity-50"
              >
                Set Out of Service
              </button>
              <button
                onClick={() => handleBulkStatusChange('Decommissioned')}
                disabled={bulkProcessing}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                Decommission
              </button>
              <div className="h-5 w-px bg-teal-200" />
              <button
                onClick={handleBulkDelete}
                disabled={bulkProcessing}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                Delete Selected
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="ml-auto text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                Clear selection
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-3 py-3 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-0.5 text-slate-400 hover:text-slate-600"
                    >
                      {selected.size === assets.length && assets.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-teal-600" />
                      ) : selected.size > 0 ? (
                        <MinusSquare className="w-4 h-4 text-teal-500" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Name
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Location
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Manufacturer
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Model
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Serial No
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Capacity
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Compliance
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className={`hover:bg-primary-50/30 cursor-pointer transition-colors ${selected.has(asset.id) ? 'bg-teal-50/40' : ''}`}
                    onClick={() => setDetailAsset(asset)}
                  >
                    <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => toggleSelect(asset.id)}
                        className="p-0.5 text-slate-400 hover:text-slate-600"
                      >
                        {selected.has(asset.id) ? (
                          <CheckSquare className="w-4 h-4 text-teal-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">{asset.name}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">
                      {asset.location_name || '-'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">
                      {asset.manufacturer || '-'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">{asset.model || '-'}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-600 font-mono text-xs">
                      {asset.serial_number || '-'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">
                      {asset.capacity_max ? `${asset.capacity_max} kg` : '-'}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={asset.status || 'Active'} />
                    </td>
                    <td className="px-5 py-3.5">
                      <ComplianceBadge status={getComplianceStatus(asset)} />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setDetailAsset(asset)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditAsset(asset);
                            setShowModal(true);
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          disabled={deleting === asset.id}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <AssetModal
          asset={editAsset}
          onClose={() => {
            setShowModal(false);
            setEditAsset(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {detailAsset && <AssetDetailPanel asset={detailAsset} onClose={() => setDetailAsset(null)} />}
    </div>
  );
};

export default AssetRegister;
